import type { BookingPreferences } from "../bookingFlowMachine"
import { ScreeningProgram, useBookingOffering } from "../hooks"
import { appointmentTypeCopy, FOLLOW_UP_HELPER_TEXT, offeringSummaryLine } from "../preferencesCopy"
import { VisitTypeCard } from "./VisitTypeCard"
import { UsaStates } from "@internal/constants"
import {
  Alert,
  Box,
  Breadcrumb,
  Breadcrumbs,
  Divider,
  ForwardIcon,
  Progress,
  Typography
} from "@carrotfertility/carotene-core"
import {
  Form,
  FormAutocomplete,
  FormButton,
  FormRadioGroup,
  type FormSelectOption
} from "@carrotfertility/carotene-core-x"
import React from "react"
import { useIntl } from "react-intl"

type SelectPreferencesPageProps = {
  program: ScreeningProgram
  onSubmitPreferences: (preferences: BookingPreferences) => void
  onReturnToProviderResults?: () => void
}

type PreferencesFormValues = {
  appointmentTypeId: string
  // FormAutocomplete stores the selected option object (or null), not a bare value.
  region: FormSelectOption | null
}

// The screen's own copy is English literal (see preferencesCopy.ts); the location-of-care
// options are localized here from the shared @internal/constants state list.
export function SelectPreferencesPage({
  program,
  onSubmitPreferences,
  onReturnToProviderResults
}: SelectPreferencesPageProps): JSX.Element {
  const intl = useIntl()
  const offeringQuery = useBookingOffering(program)

  if (offeringQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Progress />
      </Box>
    )
  }

  if (offeringQuery.isError || !offeringQuery.data) {
    return <Alert severity="error">Something went wrong loading your options. Please try again.</Alert>
  }

  const offering = offeringQuery.data
  const appointmentTypes = offering.appointmentTypes
  // The summary line and default selection follow the primary bookable type (the initial
  // consult on a first visit).
  const primaryType = appointmentTypes.find((type) => type.bookable) ?? appointmentTypes[0]

  const regionOptions = UsaStates.map((state) => ({ value: state.id, label: intl.formatMessage(state.label) }))

  const handleSubmit = (values: PreferencesFormValues) => {
    onSubmitPreferences({
      appointmentTypeId: values.appointmentTypeId,
      region: values.region ? String(values.region.value) : ""
    })
  }

  return (
    <Form<PreferencesFormValues>
      onSubmit={handleSubmit}
      defaultValues={{ appointmentTypeId: primaryType?.id ?? "", region: null }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.lg),
          maxInlineSize: 640
        })}
      >
        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Breadcrumbs>
            {onReturnToProviderResults ? (
              <Breadcrumb component="button" type="button" onClick={onReturnToProviderResults}>
                Provider results
              </Breadcrumb>
            ) : (
              <Breadcrumb>Provider results</Breadcrumb>
            )}
            <Breadcrumb>Health questions</Breadcrumb>
            <Breadcrumb currentPage>See availability</Breadcrumb>
          </Breadcrumbs>
          <Divider />
        </Box>

        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xxs) })}
        >
          <Typography variant="h1">Select your preferences to see available appointment times.</Typography>
          {primaryType && (
            <Typography color="text.secondary">{offeringSummaryLine(offering, primaryType.durationMinutes)}</Typography>
          )}
        </Box>

        <FormRadioGroup
          name="appointmentTypeId"
          label="What kind of help do you need?"
          controllerProps={{ rules: { required: "Please select a visit type." } }}
          helperText={FOLLOW_UP_HELPER_TEXT}
          radioGroupProps={{
            row: true,
            sx: (theme) => ({
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: theme.spacing(theme.tokens.spacing.sm)
            })
          }}
          options={appointmentTypes.map((type) => ({
            value: type.id,
            disabled: !type.bookable,
            label: appointmentTypeCopy(type.kind).title
          }))}
          renderItem={({ radioProps }) => {
            const type = appointmentTypes.find((candidate) => candidate.id === radioProps.value)
            return type ? <VisitTypeCard key={type.id} type={type} radioProps={radioProps} /> : null
          }}
        />

        <Box sx={{ maxInlineSize: 440 }}>
          <FormAutocomplete
            name="region"
            label="Location of care"
            controllerProps={{ rules: { required: "Please select your location of care." } }}
            fullWidth
            options={regionOptions}
          />
        </Box>

        <Box>
          <FormButton type="submit" color="secondary" endIcon={<ForwardIcon />}>
            Continue
          </FormButton>
        </Box>
      </Box>
    </Form>
  )
}
